// Calls a mutation. Create/update/remove mutations show their change on screen immediately; it's
// undone with an error toast if the server rejects it.
import { DEFAULTS } from "../../../shared/db.gen.ts";
import { canRun, type MutationArgs, type MutationName, mutations } from "../../../shared/mutations.ts";
import { toast } from "../ui/toast.tsx";
import { me } from "./session.ts";
import { type Change, store } from "./sync.ts";

export async function mutate<N extends MutationName>(name: N, args: MutationArgs<N>): Promise<boolean> {
  return (await mutateWithResult(name, args)).ok;
}

/** Like mutate(), but also returns what the server sent back (e.g. a new API token). */
export async function mutateWithResult<N extends MutationName>(
  name: N,
  args: MutationArgs<N>,
  /** Rows to show at once for custom mutations (create/update/remove work this out themselves). */
  optimistic: Change[] = [],
): Promise<{ ok: boolean; result?: unknown }> {
  const def = mutations[name];
  const parsed = def.args.safeParse(args);
  if (!parsed.success) {
    toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
    return { ok: false };
  }
  const data = parsed.data as Record<string, unknown> & { id: string };

  const changes: Change[] = [...optimistic];
  const now = new Date().toISOString();
  if (def.kind === "create" && def.table) {
    // Start from the columns' defaults so the instant row has every field the stored one will.
    const defaults = Object.fromEntries(Object.entries(DEFAULTS[def.table]).map(([k, v]) => [k, v === "now" ? now : v]));
    changes.push({ t: def.table, id: data.id, row: { ...defaults, ...data } });
  } else if (def.kind === "update" && def.table) {
    const current = store.row(def.table, data.id);
    if (current) changes.push({ t: def.table, id: data.id, row: { ...current, ...(data.patch as object), updated_at: now } });
  } else if (def.kind === "remove" && def.table) {
    changes.push({ t: def.table, id: data.id, row: null });
  }
  const key = changes.length ? store.addOverlay(changes) : null;

  try {
    const res = await fetch("/api/mutate", {
      method: "POST",
      headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
      body: JSON.stringify({ name, args: data }),
    });
    const body = (await res.json().catch(() => ({}))) as { version?: number; error?: string; result?: unknown };
    if (!res.ok) throw new Error(body.error ?? `Request failed (${res.status})`);
    if (key !== null) store.confirmOverlay(key, body.version ?? 0);
    return { ok: true, result: body.result };
  } catch (e) {
    if (key !== null) store.dropOverlay(key);
    toast.error((e as Error).message);
    return { ok: false };
  }
}

/** Whether the signed-in user may run a mutation (for hiding buttons; the server enforces it). */
export const can = (name: MutationName) => canRun(name, me()?.role);
